import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError } from "@shared/api";
import { formatDayKey, formatTimeIn } from "@shared/types";
import {
  statusLabel,
  statusKind,
  type PortalIntro,
  type PortalPlan,
  type PortalPost,
} from "./types";

/**
 * What the client sees. Mobile first, because in practice this gets opened on a
 * phone from an email.
 */
export function PortalApp() {
  const { token = "" } = useParams<{ token: string }>();
  const [intro, setIntro] = useState<PortalIntro | null>(null);
  const [plan, setPlan] = useState<PortalPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadPlan = useCallback(async () => {
    try {
      setPlan(await api.get<PortalPlan>(`/api/portal/${token}/plan`));
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setPlan(null);
      else setError(err instanceof Error ? err.message : "Something went wrong");
    }
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get<PortalIntro>(`/api/portal/${token}`);
        setIntro(data);
        if (data.signedIn) await loadPlan();
      } catch (err) {
        setError(err instanceof Error ? err.message : "That link is not valid");
      } finally {
        setLoading(false);
      }
    })();
  }, [token, loadPlan]);

  if (loading) {
    return (
      <div className="portal">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (error && !intro) {
    return (
      <div className="portal">
        <div className="portal-card">
          <h1>This link does not work</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            It may have been replaced by a newer one. Ask for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <SignIn
        token={token}
        intro={intro!}
        onSignedIn={async () => {
          setLoading(true);
          await loadPlan();
          setLoading(false);
        }}
      />
    );
  }

  return <PlanReview token={token} plan={plan} onChanged={setPlan} />;
}

/* ------------------------------------------------------------------ */

function SignIn({
  token,
  intro,
  onSignedIn,
}: {
  token: string;
  intro: PortalIntro;
  onSignedIn: () => Promise<void>;
}) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function requestCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/portal/${token}/request-code`, { email });
      setStage("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send a code");
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/portal/${token}/verify`, { email, code });
      await onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code did not work");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="portal">
      <div className="portal-card">
        <div className="portal-brand">
          <span aria-hidden="true">🕰️</span> Cuckoo
        </div>
        <h1>{intro.planTitle}</h1>
        <p className="muted" style={{ marginTop: 6 }}>
          Content for {intro.workspaceName}, ready for you to look over.
        </p>

        {error ? (
          <div className="banner error" style={{ marginTop: 16 }}>
            {error}
          </div>
        ) : null}

        {stage === "email" ? (
          <form onSubmit={requestCode} style={{ marginTop: 20 }}>
            <label htmlFor="email">Your email address</label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <p className="field-hint">
              We will send you a short code. No password to remember.
            </p>
            <button type="submit" className="primary block" disabled={busy || !email.trim()}>
              {busy ? "Sending…" : "Send me a code"}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} style={{ marginTop: 20 }}>
            <label htmlFor="code">The code we emailed you</label>
            <input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
              className="code-input"
            />
            <p className="field-hint">
              Sent to {email}. It works for ten minutes.
            </p>
            <button type="submit" className="primary block" disabled={busy || code.length < 4}>
              {busy ? "Checking…" : "Open the plan"}
            </button>
            <button
              type="button"
              className="link"
              style={{ marginTop: 14 }}
              onClick={() => {
                setStage("email");
                setCode("");
              }}
            >
              Use a different email
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function PlanReview({
  token,
  plan,
  onChanged,
}: {
  token: string;
  plan: PortalPlan;
  onChanged: (plan: PortalPlan) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const canApprove = plan.me.role === "approver";
  const waiting = plan.posts.filter((p) =>
    ["pending_review", "draft"].includes(p.status),
  ).length;

  async function decide(
    scope: "plan" | "day" | "post",
    scopeKey: string | null,
    decision: "approved" | "declined" | "changes_requested",
    text?: string,
  ) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const updated = await api.post<PortalPlan>(`/api/portal/${token}/decide`, {
        scope,
        scopeKey,
        decision,
        note: text ?? null,
      });
      onChanged(updated);
      setNote(
        decision === "approved"
          ? "Thanks. That is approved and scheduled."
          : "Thanks. We will rewrite it and send it back.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not go through");
    } finally {
      setBusy(false);
    }
  }

  async function comment(postId: string, body: string) {
    setBusy(true);
    setError(null);
    try {
      onChanged(await api.post<PortalPlan>(`/api/portal/${token}/feedback`, { postId, body }));
      setNote("Note sent.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send that note");
    } finally {
      setBusy(false);
    }
  }

  const byDay = new Map<string, PortalPost[]>();
  for (const post of plan.posts) {
    byDay.set(post.dayKey, [...(byDay.get(post.dayKey) ?? []), post]);
  }

  return (
    <div className="portal portal-wide">
      <header className="portal-header">
        <div className="portal-brand">
          <span aria-hidden="true">🕰️</span> Cuckoo
        </div>
        <h1>{plan.plan.title}</h1>
        <p className="muted">
          {plan.workspace.name} · {plan.posts.length} post
          {plan.posts.length === 1 ? "" : "s"}
          {waiting > 0 ? ` · ${waiting} waiting for you` : " · nothing left to review"}
        </p>
      </header>

      {error ? <div className="banner error">{error}</div> : null}
      {note ? <div className="banner ok">{note}</div> : null}

      {!canApprove ? (
        <div className="banner warn">
          You can read the plan and leave notes. Approving is done by{" "}
          {plan.me.name ? "another contact" : "an approver"} at {plan.workspace.name}.
        </div>
      ) : waiting > 0 ? (
        <div className="portal-card sticky">
          <p style={{ fontWeight: 500 }}>Happy with the whole week?</p>
          <p className="muted" style={{ marginTop: 4, marginBottom: 12 }}>
            You can also approve or change individual posts below.
          </p>
          <button
            className="primary block"
            disabled={busy}
            onClick={() => void decide("plan", null, "approved")}
          >
            {busy ? "Working…" : "Approve everything"}
          </button>
        </div>
      ) : null}

      {[...byDay.entries()].map(([day, posts]) => (
        <section key={day} className="portal-day">
          <h2>{formatDayKey(day)}</h2>
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              timezone={plan.workspace.timezone}
              canApprove={canApprove}
              busy={busy}
              onDecide={decide}
              onComment={comment}
            />
          ))}
        </section>
      ))}

      <footer className="portal-footer">
        Signed in as {plan.me.email}.{" "}
        <a href={`/api/portal/${token}/logout`} onClick={(e) => {
          e.preventDefault();
          void api.post(`/api/portal/${token}/logout`).then(() => window.location.reload());
        }}>
          Sign out
        </a>
      </footer>
    </div>
  );
}

function PostCard({
  post,
  timezone,
  canApprove,
  busy,
  onDecide,
  onComment,
}: {
  post: PortalPost;
  timezone: string;
  canApprove: boolean;
  busy: boolean;
  onDecide: (
    scope: "plan" | "day" | "post",
    scopeKey: string | null,
    decision: "approved" | "declined" | "changes_requested",
    note?: string,
  ) => Promise<void>;
  onComment: (postId: string, body: string) => Promise<void>;
}) {
  const [showChange, setShowChange] = useState(false);
  const [text, setText] = useState("");
  const decided = !["pending_review", "draft"].includes(post.status);

  return (
    <article className="portal-card post">
      <div className="post-head">
        <span className="muted">{formatTimeIn(post.scheduledAt, timezone)}</span>
        <span className={`pill ${statusKind(post.status)}`}>{statusLabel(post.status)}</span>
      </div>

      {post.variants.map((variant) => (
        <div key={variant.id} className="variant">
          <div className="variant-head">
            {variant.platformLabel}
            {variant.channelName ? ` · ${variant.channelName}` : ""}
          </div>
          {variant.title ? <p className="variant-title">{variant.title}</p> : null}
          <p className="variant-body">{variant.body}</p>
          {variant.hashtags.length ? (
            <p className="variant-tags">{variant.hashtags.map((h) => `#${h}`).join(" ")}</p>
          ) : null}
          {variant.firstComment ? (
            <p className="muted" style={{ marginTop: 6, fontSize: 13 }}>
              First comment: {variant.firstComment}
            </p>
          ) : null}
        </div>
      ))}

      {post.feedback.length > 0 ? (
        <div className="notes">
          {post.feedback.map((f) => (
            <p key={f.id}>
              <strong>{f.authorLabel}:</strong> {f.body}
              {f.resolvedAt ? <span className="muted"> · rewritten</span> : null}
            </p>
          ))}
        </div>
      ) : null}

      {showChange ? (
        <div style={{ marginTop: 12 }}>
          <label htmlFor={`note-${post.id}`}>What should change?</label>
          <textarea
            id={`note-${post.id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Too formal. Can you mention the new opening hours?"
            autoFocus
          />
          <div className="actions" style={{ marginTop: 10 }}>
            <button
              className="primary"
              disabled={busy || !text.trim()}
              onClick={async () => {
                if (canApprove) await onDecide("post", post.id, "changes_requested", text);
                else await onComment(post.id, text);
                setText("");
                setShowChange(false);
              }}
            >
              Send
            </button>
            <button onClick={() => setShowChange(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="actions" style={{ marginTop: 12 }}>
          {canApprove && !decided ? (
            <button
              className="primary"
              disabled={busy}
              onClick={() => void onDecide("post", post.id, "approved")}
            >
              Approve
            </button>
          ) : null}
          <button onClick={() => setShowChange(true)} disabled={busy}>
            {canApprove ? "Ask for changes" : "Leave a note"}
          </button>
        </div>
      )}
    </article>
  );
}
